import React from "react";
import { Link } from "react-router-dom";
import PublicNav from "@/components/PublicNav";
import Footer from "@/components/Footer";

export default function Suppliers() {
  return (
    <div className="bg-parchment min-h-screen" data-testid="suppliers-page">
      <PublicNav />

      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-24 pb-16">
        <div className="overline">For refineries, mills &amp; origins</div>
        <h1 className="h-serif text-5xl md:text-7xl text-ink mt-4 leading-[1.0] max-w-5xl">
          The brokerage desk for suppliers.
        </h1>
        <p className="text-sub mt-6 max-w-2xl leading-relaxed">
          We never hold inventory and we never compete with our suppliers. We
          route sanitized, fully vetted buyer requests to a tight network of
          refineries, sugar mills and coffee origins — and we get paid only when
          your cargo ships and clears.
        </p>
        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/login" className="btn-primary text-sm" data-testid="suppliers-cta-apply">
            Apply as a supplier →
          </Link>
          <Link to="/process" className="btn-ghost text-sm" data-testid="suppliers-cta-process">
            See the six-step process
          </Link>
        </div>
      </section>

      <section className="bg-parchment2 border-y border-line">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20">
          <div className="overline">What you get</div>
          <h2 className="h-serif text-4xl md:text-5xl text-ink mt-3 max-w-3xl leading-tight">
            A pre-vetted demand pipeline. No tire-kickers.
          </h2>
          <div className="mt-14 grid md:grid-cols-3 gap-10">
            {[
              { n: "01", t: "Vetted demand only",
                d: "Every buyer that reaches you has cleared KYC, OFAC SDN and OpenSanctions screening AND posted a Bank Comfort Letter. No speculative inquiries." },
              { n: "02", t: "Sanitized requests",
                d: "Buyer identity is stripped from every request you see. You evaluate spec, volume, port and tenor — not the counterparty." },
              { n: "03", t: "Bank-routed payout",
                d: "Brokerage commission is cut at the bank via IMFPA before the seller payout. You get the rest. No invoicing the buyer for our fee." },
            ].map(({ n, t, d }) => (
              <div key={n} className="border-t border-ink/15 pt-6" data-testid={`supplier-value-${n}`}>
                <div className="font-mono text-xs text-oxblood">{n}</div>
                <h3 className="h-serif text-2xl text-ink mt-2">{t}</h3>
                <p className="text-sub text-sm mt-3 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20">
        <div className="overline">Onboarding</div>
        <h2 className="h-serif text-4xl md:text-5xl text-ink mt-3 max-w-3xl leading-tight">
          Four steps. Same compliance gate as our buyers.
        </h2>
        <p className="text-sub mt-5 max-w-2xl leading-relaxed">
          We don&apos;t have a shortcut for suppliers. Every refinery, mill or
          trading house in our network has cleared the exact same KYC and
          sanctions review as the buyers we route to them — that&apos;s what makes
          the network worth being on.
        </p>
        <ol className="mt-10 space-y-6 max-w-3xl">
          {[
            { t: "Account approval", d: "Sign in with Google as a supplier. An analyst reviews your firm within 24 business hours." },
            { t: "KYC + sanctions screening", d: "Submit your corporate profile: legal entity, jurisdiction, beneficial owners, attestations. We screen everything through OpenSanctions (OFAC SDN, EU, UN, UK, global PEP lists)." },
            { t: "Trade profile", d: "Tell us what you supply: commodities, origins, monthly capacity, quality specs, payment instruments accepted, prior cargoes, banking partner." },
            { t: "Sign supplier agreements", d: "One-time signing of master NCNDA, commission agreement, AML attestation and code of conduct. They apply to every deal — no deal-by-deal paperwork." },
          ].map((s, i) => (
            <li key={i} className="grid md:grid-cols-12 gap-6 py-6 border-t border-line">
              <div className="md:col-span-2 font-mono text-sm text-oxblood">STEP 0{i + 1}</div>
              <div className="md:col-span-10">
                <h3 className="h-serif text-2xl text-ink">{s.t}</h3>
                <p className="text-sub text-sm mt-2 leading-relaxed">{s.d}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="bg-ink text-parchment grain relative">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 flex flex-col md:flex-row items-start md:items-end justify-between gap-10">
          <div>
            <div className="overline !text-brass">Apply to join the network</div>
            <h2 className="h-serif text-4xl md:text-5xl mt-3 max-w-3xl leading-tight">
              Refinery, mill, origin or trading house? Start your supplier intake.
            </h2>
          </div>
          <Link to="/login" className="btn-brass" data-testid="suppliers-bottom-cta">
            Apply as a supplier →
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
